import { format } from "date-fns";
import { api } from "./axios";
import { PlanFrequency } from "../types";
import type {
  FamilyMemberReportSummary,
  OngoingXSIP,
  PortfolioSummary,
  PortfolioValuationHolding,
  ProductType,
  RecentTransactionsReportTransaction,
} from "../types/portfolio";

// Legacy synchronous PDF download (deprecated)
export const downloadReport = async (url: string): Promise<Blob> => {
  return api.get("download-pdf/", {
    params: {
      url,
    },
    responseType: "blob",
  });
};

// New async PDF generation APIs
export interface InitiatePdfGenerationResponse {
  task_id: string;
  status: "PENDING";
  message: string;
}

export interface PdfStatusResponse {
  task_id: string;
  status: "PENDING" | "SUCCESS" | "FAILURE";
  result: {
    status: "in_progress" | "completed" | "failed";
    download_ready?: boolean;
    error?: string;
    storage_key?: string;
    filename?: string;
    size?: number;
    url?: string;
  };
}

export const initiatePdfGeneration = async (
  url: string,
  dimensions?: { width: number; height: number }
): Promise<InitiatePdfGenerationResponse> => {
  const response = await api.get("async-download-pdf/", {
    params: {
      url,
      ...(dimensions
        ? { width: dimensions.width, height: dimensions.height }
        : {}),
    },
  });
  return response;
};

export const checkPdfStatus = async (
  taskId: string
): Promise<PdfStatusResponse> => {
  const response = await api.get("pdf-status/", {
    params: { task_id: taskId },
  });
  return response;
};

export const downloadPdf = async (taskId: string): Promise<Blob> => {
  const response = await api.get("pdf-download/", {
    params: { task_id: taskId },
    responseType: "blob",
  });
  return response;
};

export type ReportType =
  | "transactions"
  | "recent_transactions"
  | "capital_gains"
  | "holdings"
  | "statement";
export type ReportFileType = "pdf" | "excel" | "csv" | "json";

interface BaseReportDataPayload {
  file_type: ReportFileType;
  start_date?: string;
  end_date?: string;
}

interface TransactionsReportDataPayload extends BaseReportDataPayload {
  type: "transactions";
  investment_account_guid: string;
}

interface RecentTransactionsReportDataPayload extends BaseReportDataPayload {
  type: "recent_transactions";
  investment_account_guid?: string;
  family_id?: number;
  additional_details?: {
    order_status?: string;
    product_type?: string[];
  };
}

interface CapitalGainsReportDataPayload extends BaseReportDataPayload {
  type: "capital_gains";
  investment_account_guid: string;
  source?: "INTERNAL" | "EXTERNAL" | "OVERALL";
}

// Holdings/statement reports can be scoped to a single client's investment
// account or, for the family portfolio valuation report, to a whole family.
interface HoldingsReportDataPayload extends BaseReportDataPayload {
  type: "holdings" | "statement";
  source: "INTERNAL" | "EXTERNAL" | "OVERALL";
  investment_account_guid?: string;
  family_id?: number;
  additional_details?: {
    include_transactions?: boolean;
  };
}

export type GenerateReportDataPayload<T extends ReportType> =
  T extends "transactions"
    ? TransactionsReportDataPayload
    : T extends "recent_transactions"
      ? RecentTransactionsReportDataPayload
      : T extends "capital_gains"
        ? CapitalGainsReportDataPayload
        : HoldingsReportDataPayload;

/** JSON payload from `reports/generate/` for portfolio valuation (holdings / statement). `member_summary` is only present for family-scoped reports. */
export type PortfolioValuationReportJson = {
  summary?: PortfolioSummary | null;
  holdings?: PortfolioValuationHolding[];
  ongoing_xsip?: OngoingXSIP[];
  member_summary?: FamilyMemberReportSummary[];
};

/** JSON payload from `reports/generate/` for the order history / recent transactions report. */
export type RecentTransactionsReportJson = {
  summary?: {
    order_count: number;
    total_amount: number;
    total_invested: number;
    total_redeemed: number;
    total_switched: number;
    status_counts: Record<string, number>;
    product_types: ProductType[];
    order_statuses: string[];
  } | null;
  transactions?: RecentTransactionsReportTransaction[];
};

export type ReportResponse<
  T extends ReportType,
  F extends ReportFileType,
> = F extends "json"
  ? T extends "capital_gains"
    ? {
        total_capital_gain: number;
        long_term_capital_gain: number;
        short_term_capital_gain: number;
        equity_ltcg: number;
        equity_stcg: number;
        debt_ltcg: number;
        debt_stcg: number;
        hybrid_ltcg: number;
        hybrid_stcg: number;
        scheme_level: Array<{
          scheme_name: string;
          capital_gain: number;
          long_term_capital_gain: number;
          short_term_capital_gain: number;
          isin: string;
          amc_morning_star_code: string;
          sell_date: string;
          folio_number: string;
          purchase_price: string;
          sell_price: string;
          fund_type: string;
        }>;
        transaction_details: Array<{
          scheme_isin: string;
          scheme_name: string;
          folio_number: string;
          amc_morning_star_code: string;
          purchase_date: string | null;
          purchase_units: number;
          purchase_value: number;
          acquisition_value: number;
          redemption_date: string | null;
          redemption_units: number;
          redemption_value: number;
          fund_level_category_name: string;
          stcg: number;
          ltcg: number;
          fund_type: string;
          equity_percent: number;
          is_total_row: boolean | null;
        }>;
      }
    : T extends "holdings" | "statement"
      ? PortfolioValuationReportJson
      : T extends "recent_transactions"
        ? RecentTransactionsReportJson
        : never
  : {
      report_url: string;
    };

export const generateReportData = <
  T extends ReportType,
  F extends ReportFileType,
>(
  payload: GenerateReportDataPayload<T>
): Promise<ReportResponse<T, F>> => {
  return api.post("reports/generate/", {
    start_date: format(new Date(), "yyyy-MM-dd"),
    end_date: format(new Date(), "yyyy-MM-dd"),
    ...payload,
  });
};

export const getCalendarDate = ({
  date,
  workingDays,
}: {
  date: string;
  workingDays: string;
}): Promise<{ result_date: string }> => {
  return api.post("nse/calendar/", { date, workingDays });
};

export const getMinStartDate = (params: {
  frequency: "DAILY" | "WEEKLY" | "MONTHLY";
  first_order_today: boolean;
}) => {
  return api.get("xsip/min-start-date/", {
    params,
  });
};

export const getPlanMinStartDate = (params: {
  planType: "XSIP" | "SWP" | "STP";
  frequency: PlanFrequency;
  firstOrderToday: boolean;
}) => {
  return api.get("plan-min-start-date/", {
    params: {
      plan_type: params.planType,
      frequency: params.frequency,
      first_order_today: params.firstOrderToday,
    },
  });
};

interface DistributorReportPayload {
  file_type: "excel";
  type: "brokerage_report";
  start_date: string;
  end_date: string;
  arn_code: string;
}

export const generateDistributorReport = (
  payload: DistributorReportPayload
) => {
  return api.post("distributor-reports/generate/", payload);
};

export const fetchStates = (): Promise<
  {
    state_name: string;
    code: string;
    sr_no: number;
  }[]
> => {
  return api.get("states/");
};

export const fetchCountries = (): Promise<
  {
    sr_no: number;
    code: string;
    details: string;
    nse_code?: string;
  }[]
> => api.get("countries/");
